import { useState, useCallback } from 'react';

/**
 * Custom hook för hantering av expanderade/kollapsa sektioner med localStorage-persistens
 * 
 * Hanterar:
 * - Expanderade estimat-kategorier (projektering, byggoffert, etc.)
 * - Expanderade info-sektioner (beställare, information, intervjufrågor, etc.)
 * - Automatisk sparning till localStorage
 * 
 * @param {string} leadId - Lead/Procurement ID för att skapa unika localStorage-nycklar
 */
// Default expanderade kategorier (minimerade från början).
// Ligger på modulnivå: innehållet beror inte på hookens argument, och som
// konstant blir det en stabil referens som getStoredExpandedSections nedan
// inte behöver ta med i sin beroendelista.
const defaultExpandedSections = {
  projektering: false,
  byggoffert: false,
  sidoentreprenorer: false,
  inkopFastInredning: false
};

const useExpandedSections = (leadId) => {
  // Hjälpfunktion för att hämta expanderade kategorier
  // Returnerar alltid defaults (alla expanderade) - ignorerar localStorage
  const getStoredExpandedSections = useCallback(() => {
    // Kopia, så att konstanten på modulnivå inte kan muteras av en anropare
    return { ...defaultExpandedSections };
  }, []);

  // Hjälpfunktion för att hämta sparade expanderade info-sektioner
  const getStoredExpandedInfoSections = useCallback(() => {
    try {
      const stored = localStorage.getItem(`estimat_expanded_info_sections_${leadId}`);
      return stored ? JSON.parse(stored) : {
        beställare: true,
        information: true,
        intervjufrågor: true,
        hjälpInredningsarkitekt: false,
        kundaktivitet: false,
        uppföljning: false
      };
    } catch (error) {
      console.warn('Kunde inte läsa sparade info-sektioner från localStorage:', error);
      return {
        beställare: true,
        information: true,
        intervjufrågor: true,
        hjälpInredningsarkitekt: false,
        kundaktivitet: false,
        uppföljning: false
      };
    }
  }, [leadId]);

  // State för expanderade sektioner
  const [expandedSections, setExpandedSections] = useState(getStoredExpandedSections);
  const [expandedInfoSections, setExpandedInfoSections] = useState(getStoredExpandedInfoSections);

  /**
   * Togglar en estimat-kategori (projektering, byggoffert, etc.)
   * @param {string} categoryKey - Nyckel för kategorin att toggla
   */
  const toggleSection = useCallback((categoryKey) => {
    setExpandedSections(prev => {
      const newExpanded = {
        ...prev,
        [categoryKey]: !prev[categoryKey]
      };
      
      // Spara till localStorage
      try {
        localStorage.setItem(`estimat_expanded_sections_${leadId}`, JSON.stringify(newExpanded));
      } catch (error) {
        console.warn('Kunde inte spara expanderade sektioner till localStorage:', error);
      }
      
      return newExpanded;
    });
  }, [leadId]);

  /**
   * Togglar en info-sektion (beställare, information, intervjufrågor, etc.)
   * @param {string} sectionKey - Nyckel för sektionen att toggla
   */
  const toggleInfoSection = useCallback((sectionKey) => {
    setExpandedInfoSections(prev => {
      const newExpanded = {
        ...prev,
        [sectionKey]: !prev[sectionKey]
      };
      
      // Spara till localStorage
      try {
        localStorage.setItem(`estimat_expanded_info_sections_${leadId}`, JSON.stringify(newExpanded));
      } catch (error) {
        console.warn('Kunde inte spara expanderade info-sektioner till localStorage:', error);
      }
      
      return newExpanded;
    });
  }, [leadId]);

  return {
    // State
    expandedSections,
    expandedInfoSections,
    
    // Setters (exponerade för direktmanipulering om nödvändigt)
    setExpandedSections,
    setExpandedInfoSections,
    
    // Actions
    toggleSection,
    toggleInfoSection,
    
    // Getters (för att läsa från localStorage)
    getStoredExpandedSections,
    getStoredExpandedInfoSections,
  };
};

export default useExpandedSections;
